import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../../shared/api/client';
import { ApiError } from '../../shared/api/problem';
import { useCurrentUser } from './useCurrentUser';

/**
 * The add-ons this tenant has bought, from `GET /api/v1/auth/features`.
 *
 * **A different question from usePermissions.** A permission says what this user may do inside
 * the tenant; a flag says whether the tenant has the capability at all. Both are checked
 * server-side on every gated request, and both are hints here.
 *
 * **Not keyed by warehouse**, unlike permissions. An add-on is sold to the tenant, so the answer
 * is the same in every warehouse and keying it by one would refetch for nothing on every switch.
 *
 * **FAILS OPEN on error**, exactly as usePermissions does and for the same reason: "we could not
 * find out" is not "you have bought nothing", and treating it as such would strip the screens
 * from a customer who owns them. The server refuses anything genuinely unentitled, so the worst
 * case is a screen that turns out to answer 403.
 */

export const FEATURES_KEY = ['auth', 'features'] as const;

/** The seven, from Packaging_CoreVsAddOns.md §3.1. */
export const FEATURE_ADVANCED_UNITS = 'addon.advanced-units';
export const FEATURE_TRACEABILITY = 'addon.traceability';
export const FEATURE_MANUFACTURING = 'addon.manufacturing';
export const FEATURE_PLANNING = 'addon.planning';
export const FEATURE_PRICING = 'addon.pricing';
export const FEATURE_QUICKBOOKS = 'addon.quickbooks';
export const FEATURE_INTEGRATIONS_3PL = 'addon.integrations-3pl';

export async function getFeatures(signal?: AbortSignal): Promise<string[]> {
  try {
    const { data } = await apiRequest<{ features: string[] }>('/v1/auth/features', {
      method: 'GET',
      signal,
      credentials: 'include',
    });
    return data.features;
  } catch (err) {
    // Anonymous is a normal answer, same as /me and /permissions.
    if (err instanceof ApiError && err.status === 401) return [];
    throw err;
  }
}

export function useFeatures() {
  const { data: user } = useCurrentUser();

  const query = useQuery<string[]>({
    queryKey: FEATURES_KEY,
    queryFn: ({ signal }) => getFeatures(signal),
    enabled: Boolean(user),
    // Longer than permissions' zero: an add-on changes when somebody buys or drops one, which is
    // a console action taken minutes apart from the user noticing, not a grant an administrator
    // makes while the user waits. A minute keeps a purchase feeling immediate without asking on
    // every mount.
    staleTime: 60_000,
    retry: false,
  });

  const held = new Set(query.data ?? []);

  return {
    /** True while the answer is unknown — see the note about failing open. */
    has: (key: string) => (query.isError ? true : held.has(key)),
    held,
    isLoading: query.isLoading,
    isUnknown: query.isError,
    refetch: query.refetch,
  };
}
